/**
 * LocationsImport — bulk creation, option 1d ("one canvas").
 *
 * Built 2026-08-07 (Nick) from `Locations - Admin & Bulk Creation.dc.html`.
 * One surface serves every route: upload a file, paste a block out of a
 * spreadsheet, type blank rows by hand, or clone an existing location N times —
 * all of them land as rows in the same grid, and fixing a bad cell is typing in
 * it. No wizard steps, no mode.
 *
 * Validation is the point of the screen, and it is NOT client-side theatre:
 * every keystroke settles into a real `validateLocationBatch` dry run through
 * the typed client, so the "will create / will update / rejected" counts are
 * the server's verdict and the commit cannot contradict the preview. Each row
 * is validated against ITS OWN country's rules (a UK postcode and a Japanese
 * one are both right, in different rows of the same file).
 *
 * Commit goes through `bulkCreateLocations`, awaited, with rejected rows coming
 * back as a downloadable CSV — the operator's chosen error policy.
 */
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Btn, Breadcrumb, Dropdown, FileUpload, Icon, InlineEmptyState, Input, Spinner, Textarea, Toggle, T, type QueuedFile } from "@v0y4ge/jrni-ui";
import { validateLocationBatch, bulkCreateLocations } from "@v0y4ge/api-client";
import type { LocationBatchIssue } from "@v0y4ge/api-contracts";
import { LOCATIONS_OPTS } from "../../shell/request-opts";
import { Screen, PageHeader } from "../../shell/chrome";
import { useIsMobile } from "../../shell/useResponsive";
import { can } from "../../shell/access";
import { useNav } from "../../shell/router";
import { toast } from "../../shell/toast";
import { logAction } from "../../lib/audit";
import { useLocations } from "./useLocations";
import { useT, t as i18nT } from "../../lib/i18n";
import {
  parseDelimited, autoMapColumns, IMPORT_FIELDS, templateCsv, downloadText,
  type LocField,
} from "./location-validation";

type Draft = Record<string, string | number | boolean | undefined>;

interface Verdict {
  total: number;
  createCount: number;
  updateCount: number;
  rejectCount: number;
  issues: LocationBatchIssue[];
}

const COL_W = 168;
const SEV_TONE: Record<string, { hue: string; bg: string; br: string; fg: string }> = {
  error: { hue: "#B0093A", bg: "#fff", br: "#F5C9D5", fg: "#B0093A" },
  warning: { hue: "#8A6300", bg: "#FFF6E5", br: "#F2E6C7", fg: "#8A6300" },
  info: { hue: T.p1, bg: T.p4, br: "#D5C6FA", fg: T.p1 },
};

export default function LocationsImport() {
  const t = useT();
  const nav = useNav();
  const canWrite = can("locations.write");
  const { locations, reload } = useLocations();

  const [headers, setHeaders] = useState<string[]>([]);
  const [mapping, setMapping] = useState<(LocField | null)[]>([]);
  const [rows, setRows] = useState<string[][]>([]);
  const [sourceName, setSourceName] = useState<string>("");
  const [verdict, setVerdict] = useState<Verdict | null>(null);
  const [checking, setChecking] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [issuesOnly, setIssuesOnly] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [cloneId, setCloneId] = useState("");
  const [cloneN, setCloneN] = useState(3);
  const [uploadFiles, setUploadFiles] = useState<QueuedFile[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  /* Below ~1100px a hard-300px issues panel starved the grid to 334px of its
     1552px width — 7 of 9 columns, and the per-cell error treatment the whole
     "fix it in place" design depends on, sat off-screen (verifier, 2026-08-07).
     The panel leaves the row entirely and becomes a strip above the grid. */
  const narrow = useIsMobile(1100);

  /* rows + mapping → the contract's LocationBatchRow shape. */
  const drafts: Draft[] = useMemo(() => rows.map((r, i) => {
    const d: Draft = { rowNumber: i + 1 };
    mapping.forEach((field, ci) => {
      if (!field) return;
      const v = (r[ci] ?? "").trim();
      if (v) d[field] = v;
    });
    return d;
  }), [rows, mapping]);

  /* Debounced dry run — the server is the authority on the counts. */
  const runCheck = useCallback(async (input: Draft[]) => {
    if (!input.length) { setVerdict(null); return; }
    setChecking(true);
    try {
      const res = await (validateLocationBatch as unknown as (b: unknown, o: unknown) => Promise<Verdict>)(
        { rows: input, autoFix: false }, LOCATIONS_OPTS,
      );
      setVerdict(res);
    } catch (e) {
      setVerdict(null);
      toast({
        tone: "danger",
        title: t("locations.import.couldnTCheckTheRows", "Couldn't check the rows"),
        subtitle: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setChecking(false);
    }
  }, [t]);

  useEffect(() => {
    if (!drafts.length) { setVerdict(null); return; }
    const h = setTimeout(() => { void runCheck(drafts); }, 350);
    return () => clearTimeout(h);
  }, [drafts, runCheck]);

  /* issue lookup: "row:field" → the worst issue on that cell. */
  const cellIssues = useMemo(() => {
    const m = new Map<string, LocationBatchIssue>();
    for (const i of verdict?.issues ?? []) {
      const key = `${i.rowNumber}:${i.field}`;
      const prev = m.get(key);
      if (!prev || (prev.severity !== "error" && i.severity === "error")) m.set(key, i);
    }
    return m;
  }, [verdict]);

  const rowHasError = useMemo(() => {
    const s = new Set<number>();
    for (const i of verdict?.issues ?? []) if (i.severity === "error") s.add(i.rowNumber);
    return s;
  }, [verdict]);

  /** Issues grouped by (code + field), newest count first — the to-do list. */
  const grouped = useMemo(() => {
    const g = new Map<string, { code: string; field: string; severity: string; message: string; rows: number[]; fixable: number }>();
    for (const i of verdict?.issues ?? []) {
      const key = `${i.code}:${i.field}`;
      const hit = g.get(key);
      if (hit) { hit.rows.push(i.rowNumber); if (i.suggestion) hit.fixable++; }
      else g.set(key, { code: i.code, field: i.field, severity: i.severity, message: i.message, rows: [i.rowNumber], fixable: i.suggestion ? 1 : 0 });
    }
    return [...g.values()].sort((a, b) => b.rows.length - a.rows.length);
  }, [verdict]);

  const loadTable = (headerRow: string[], dataRows: string[][], name: string) => {
    setHeaders(headerRow);
    setMapping(autoMapColumns(headerRow));
    setRows(dataRows);
    setSourceName(name);
    setIssuesOnly(false);
  };

  const onFile = (file: File) => {
    if (/\.(xlsx|xls)$/i.test(file.name)) {
      toast({
        tone: "warn",
        title: t("locations.import.saveItAsCsvFirst", "Save it as CSV first"),
        subtitle: t("locations.import.spreadsheetBinariesArenTReadHere", "Spreadsheet binaries aren't read here yet — export the sheet as CSV, or paste the rows straight into the grid."),
      });
      return;
    }
    setUploadFiles([{ id: `${file.name}:${file.size}:${file.lastModified}`, file }]);
    const reader = new FileReader();
    reader.onload = () => {
      const { headers: h, rows: r } = parseDelimited(String(reader.result ?? ""));
      if (!h.length) {
        toast({ tone: "danger", title: t("locations.import.thatFileHadNoRows", "That file had no rows"), subtitle: file.name });
        return;
      }
      loadTable(h, r, file.name);
    };
    reader.readAsText(file);
  };

  const onFilesAdded = (files: File[]) => {
    const file = files[0];
    if (file) onFile(file);
  };

  const addBlankRows = (n: number) => {
    const h = headers.length ? headers : IMPORT_FIELDS.map((f) => f.label);
    const m = mapping.length ? mapping : IMPORT_FIELDS.map((f) => f.key);
    setHeaders(h);
    setMapping(m);
    setRows((r) => [...r, ...Array.from({ length: n }, () => h.map(() => ""))]);
    if (!sourceName) setSourceName(i18nT("locations.import.typedByHand", "Typed by hand"));
  };

  const applyPaste = () => {
    const { headers: h, rows: r } = parseDelimited(pasteText);
    if (!h.length) {
      toast({ tone: "danger", title: t("locations.import.nothingToPaste", "Nothing to paste"), subtitle: t("locations.import.copyTheHeaderRowTooSoTheColumns", "Copy the header row too, so the columns can be matched.") });
      return;
    }
    loadTable(h, r, i18nT("locations.import.pastedRows", "Pasted rows"));
    setPasteOpen(false);
    setPasteText("");
  };

  const applyClone = () => {
    const src = locations.find((l) => (l.raw?.id ?? l.id) === cloneId);
    if (!src) return;
    const raw = (src.raw ?? {}) as Record<string, unknown>;
    const h = IMPORT_FIELDS.map((f) => f.label);
    const m = IMPORT_FIELDS.map((f) => f.key);
    const made = Array.from({ length: Math.max(1, Math.min(50, cloneN)) }, (_, i) =>
      IMPORT_FIELDS.map((f) => {
        if (f.key === "name") return `${String(raw.name ?? src.name)} ${i + 2}`;
        if (f.key === "externalCode") return ""; // a store code is unique by definition
        return String(raw[f.key] ?? "");
      }));
    setHeaders(h);
    setMapping(m);
    setRows((r) => (headers.length ? [...r, ...made] : made));
    setSourceName(i18nT("locations.import.clonedRows", "Cloned rows"));
  };

  /** Apply every server-proposed suggestion (the "Fix all" affordance). */
  const fixAll = async () => {
    setChecking(true);
    try {
      const res = await (validateLocationBatch as unknown as (b: unknown, o: unknown) => Promise<Verdict & { rows?: Draft[] }>)(
        { rows: drafts, autoFix: true }, LOCATIONS_OPTS,
      );
      const fixed = res.rows ?? [];
      setRows((prev) => prev.map((r, ri) => {
        const f = fixed[ri];
        if (!f) return r;
        return r.map((cell, ci) => {
          const field = mapping[ci];
          if (!field) return cell;
          const v = f[field];
          return v === undefined || v === null ? cell : String(v);
        });
      }));
      toast({ tone: "success", title: t("locations.import.suggestionsApplied", "Suggestions applied"), subtitle: t("locations.import.reCheckingTheRows", "Re-checking the rows…") });
    } catch (e) {
      toast({ tone: "danger", title: t("locations.import.couldnTApplyTheFixes", "Couldn't apply the fixes"), subtitle: e instanceof Error ? e.message : String(e) });
    } finally {
      setChecking(false);
    }
  };

  const buildRejectsCsv = () => {
    const byRow = new Map<number, string[]>();
    for (const i of verdict?.issues ?? []) {
      if (i.severity !== "error") continue;
      const list = byRow.get(i.rowNumber) ?? [];
      list.push(`${i.field}: ${i.message}`);
      byRow.set(i.rowNumber, list);
    }
    const esc = (s: string) => (/[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);
    const cols = IMPORT_FIELDS.map((f) => f.key);
    const lines = [["rowNumber", ...IMPORT_FIELDS.map((f) => f.label), "_error"].join(",")];
    drafts.forEach((d) => {
      const errs = byRow.get(Number(d.rowNumber));
      if (!errs) return;
      lines.push([String(d.rowNumber), ...cols.map((c) => esc(String(d[c] ?? ""))), esc(errs.join(" · "))].join(","));
    });
    return lines.join("\r\n");
  };

  const commit = async () => {
    if (committing || !verdict) return;
    setCommitting(true);
    try {
      const res = await (bulkCreateLocations as unknown as (b: unknown, o: unknown) => Promise<{
        createdCount: number; updatedCount: number; rejectedCount: number; rejectsCsv?: string | null;
      }>)({ rows: drafts, onDuplicateExternalCode: "update" }, LOCATIONS_OPTS);
      logAction("location", "bulk", `${res.createdCount} created, ${res.updatedCount} updated`, "created");
      reload();
      if (res.rejectsCsv) downloadText("locations-rejects.csv", res.rejectsCsv);
      toast({
        tone: res.rejectedCount ? "warn" : "success",
        title: t("locations.import.locationsAreLiveCount", "{count, plural, one {# location is live} other {# locations are live}}", { count: res.createdCount + res.updatedCount }),
        subtitle: [t("locations.import.createdCount", "{count} created", { count: res.createdCount }), t("locations.import.updatedCount", "{count} updated", { count: res.updatedCount }), res.rejectedCount ? t("locations.import.rejectedCount", "{count} rejected (file downloaded)", { count: res.rejectedCount }) : ""].filter(Boolean).join(" · "),
      });
      setCommitting(false);
      nav.go("locations");
    } catch (e) {
      setCommitting(false);
      toast({
        tone: "danger",
        title: t("locations.import.importFailed", "Import failed"),
        subtitle: e instanceof Error ? e.message : String(e),
      });
    }
  };

  const visibleRows = rows
    .map((r, i) => ({ r, n: i + 1 }))
    .filter(({ n }) => !issuesOnly || (verdict?.issues ?? []).some((x) => x.rowNumber === n));

  const chip = (label: string, icon: React.ReactNode, onClick: () => void, on?: boolean) => (
    <Btn key={label} type="button" size="sm" variant={on ? "primary" : "secondary"} icon={icon} onClick={onClick} disabled={!canWrite}>
      {label}
    </Btn>
  );

  const canCommit = !!verdict && verdict.total > 0 && (verdict.createCount + verdict.updateCount) > 0 && !committing && canWrite;

  return (
    <Screen rail="locations">
      <PageHeader
        title={t("locations.import.addLocationsInBulk", "Add locations in bulk")}
        subtitle={t("locations.import.uploadPasteTypeOrClone", "Upload a file, paste from a spreadsheet, type rows by hand, or clone an existing location. Every row is checked against its own country's rules.")}
        breadcrumb={<Breadcrumb items={[{ label: <><Icon.arrow dir="left" size={11} /> {t("locations.editorPage.locations", "Locations")}</>, onClick: () => nav.go("locations")}]} />}
        actions={
          <>
            <Btn variant="secondary" icon={<Icon.download size={14} />} onClick={() => downloadText("locations-template.csv", templateCsv())}>
              {t("locations.import.template", "Template")}
            </Btn>
            {verdict?.rejectCount ? (
              <Btn variant="secondary" icon={<Icon.download size={14} />} onClick={() => downloadText("locations-rejects.csv", buildRejectsCsv())}>
                {t("locations.import.downloadRejects", "Download rejects")} ({verdict.rejectCount})
              </Btn>
            ) : null}
            <Btn icon={<Icon.check size={14} />} disabled={!canCommit} onClick={() => void commit()}>
              {committing
                ? t("locations.import.creating", "Creating…")
                : t("locations.import.text", "{arg1} {arg2}", { arg1: t("locations.import.create", "Create"), arg2: verdict ? verdict.createCount + verdict.updateCount : 0 })}
            </Btn>
          </>
        }
      />

      {/* Source strip */}
      <div style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 8, padding: "11px 24px", borderBottom: `1px solid ${T.border}`, background: T.white, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: ".06em", textTransform: "uppercase", color: T.soft, marginRight: 2 }}>
          {t("locations.import.addRowsFrom", "Add rows from")}
        </span>
        {chip(t("locations.import.upload", "Upload"), <Icon.upload size={13} />, () => fileRef.current?.click(), !!sourceName && /\.csv|\.tsv|\.txt/i.test(sourceName))}
        {chip(t("locations.import.paste", "Paste"), <Icon.copy size={13} />, () => setPasteOpen((v) => !v), pasteOpen)}
        {chip(t("locations.import.blankRows", "Blank rows"), <Icon.plus size={13} />, () => addBlankRows(5))}
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Dropdown size="sm" aria-label={i18nT("locations.import.cloneAnExistingLocation", "Clone an existing location…")} value={cloneId || null}
            onChange={setCloneId} disabled={!canWrite} placeholder={i18nT("locations.import.cloneAnExistingLocation", "Clone an existing location…")}
            options={locations.map((l) => ({ value: String(l.raw?.id ?? l.id), label: l.name }))}
            style={{ width: 190 }} />
          <Input type="number" min={1} max={50} value={cloneN} disabled={!canWrite} size="sm" aria-label={i18nT("locations.import.numberOfClones", "Number of clones")}
            onChange={(e) => setCloneN(Math.max(1, Math.min(50, Number(e.target.value) || 1)))} style={{ width: 56 }} />
          {chip(t("locations.import.clone", "Clone"), <Icon.copy size={13} />, applyClone)}
        </span>
        <span style={{ flex: 1 }} />
        {sourceName ? (
          <span style={{ fontSize: 12.5, color: T.soft }}>
            {rows.length} {rows.length === 1 ? t("locations.import.row", "row") : t("locations.import.rows", "rows")} · <span style={{ fontFamily: T.fontMono, fontSize: 12, color: T.n2 }}>{sourceName}</span>
          </span>
        ) : null}
      </div>

      {pasteOpen ? (
        <div style={{ flexShrink: 0, padding: "12px 24px", borderBottom: `1px solid ${T.border}`, background: T.canvas }}>
          <Textarea value={pasteText} onChange={(e) => setPasteText(e.target.value)} rows={4} mono
            placeholder={i18nT("locations.import.pastePlaceholder", "Paste rows here, header row included — tab- or comma-separated.")} />
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <Btn size="sm" onClick={applyPaste}>{t("locations.import.loadRows", "Load rows")}</Btn>
            <Btn size="sm" variant="secondary" onClick={() => { setPasteOpen(false); setPasteText(""); }}>{t("common.actions.cancel", "Cancel")}</Btn>
          </div>
        </div>
      ) : null}

      {/* Body */}
      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: narrow ? "column" : "row", gap: 14, padding: "14px 24px 20px", boxSizing: "border-box", overflowY: narrow ? "auto" : "visible" }}>
        <FileUpload ref={fileRef} files={uploadFiles} onFilesAdded={onFilesAdded} accept=".csv,.tsv,.txt,.xlsx,.xls" multiple={false}
          disabled={!canWrite} label={t("locations.import.dropACsvHere", "Drop a CSV here")}
          hint={t("locations.import.orPasteBlankClone", "…or paste from a spreadsheet, add blank rows, or clone an existing location.")}
          onFileRemoved={() => setUploadFiles([])} style={rows.length ? { display: "none" } : { flex: 1 }} />
        {!rows.length ? null : (
          <>
            {/* Grid. Stacked (narrow) it must NOT flex for vertical space — the
                panel above it would squeeze it to nothing on a short viewport;
                it takes a real minimum height and the body scrolls instead. */}
            <div style={{ flex: narrow ? "0 0 auto" : 1, minWidth: 0, minHeight: narrow ? 340 : 0, background: T.white, borderRadius: 10, border: `1px solid ${T.border}`, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <div style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderBottom: `1px solid ${T.border}` }}>
                <Toggle checked={issuesOnly} onChange={setIssuesOnly} disabled={!canWrite}
                  label={t("locations.import.onlyRowsWithIssues", "Only rows with issues")} />
                <span style={{ fontSize: 12.5, color: T.soft }}>
                  {t("locations.import.showing", "Showing")} {visibleRows.length} {t("locations.import.of", "of")} {rows.length}
                </span>
                <span style={{ flex: 1 }} />
                <span style={{ fontSize: 12, color: T.soft }}>
                  {checking ? t("locations.import.checking", "Checking…") : t("locations.import.everyHeaderIsADropdown", "Every header is a dropdown — remap a column without leaving the grid")}
                </span>
              </div>

              <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                <div style={{ minWidth: 40 + headers.length * COL_W }}>
                  {/* header */}
                  <div style={{ display: "flex", position: "sticky", top: 0, zIndex: 2, background: T.n6, borderBottom: `1px solid ${T.border}` }}>
                    <span style={{ width: 40, flexShrink: 0 }} />
                    {headers.map((h, ci) => (
                      <span key={ci} style={{ width: COL_W, flexShrink: 0, padding: "6px 8px", boxSizing: "border-box" }}>
                        <span style={{ display: "block", fontSize: 10.5, color: T.n3, fontFamily: T.fontMono, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{h || "—"}</span>
                        <Dropdown size="sm" aria-label={t("locations.import.columnLabel", "{name} {number}", { name: h || t("locations.import.column", "Column"), number: ci + 1 })} value={mapping[ci] ?? null} disabled={!canWrite}
                          onChange={(value) => setMapping((m) => m.map((x, xi) => (xi === ci ? (value || null) as LocField | null : x)))}
                          placeholder={i18nT("locations.import.donTImport", "Don't import")}
                          options={[{ value: "", label: i18nT("locations.import.donTImport", "Don't import") }, ...IMPORT_FIELDS.map((f) => ({ value: f.key, label: i18nT(f.labelKey, f.label) }))]}
                          style={{ width: "100%", marginTop: 2 }} />
                      </span>
                    ))}
                  </div>

                  {visibleRows.map(({ r, n }) => (
                    <div key={n} style={{ display: "flex", borderBottom: `1px solid ${T.n6}` }}>
                      <span style={{ width: 40, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontFamily: T.fontMono, color: rowHasError.has(n) ? "#B0093A" : T.n3, background: rowHasError.has(n) ? "#FDF2F5" : T.canvas }}>{n}</span>
                      {headers.map((_, ci) => {
                        const field = mapping[ci];
                        const issue = field ? cellIssues.get(`${n}:${field}`) : undefined;
                        const bad = issue?.severity === "error";
                        const warn = issue?.severity === "warning";
                        return (
                          <span key={ci} style={{ width: COL_W, flexShrink: 0, boxSizing: "border-box", padding: 0, position: "relative" }}>
                            <Input
                              value={r[ci] ?? ""} readOnly={!canWrite} title={issue?.message ?? ""} aria-label={t("locations.import.cellLabel", "{column}, row {row}", { column: field ? i18nT(IMPORT_FIELDS.find((f) => f.key === field)?.labelKey ?? "", field) : headers[ci] || t("locations.import.column", "Column"), row: n })}
                              onChange={(e) => {
                                const v = e.target.value;
                                setRows((prev) => prev.map((row, ri) => (ri === n - 1 ? row.map((c, cj) => (cj === ci ? v : c)) : row)));
                              }}
                              size="sm" style={{
                                width: "100%", fontFamily: field && ["postalCode", "country", "timezone", "externalCode", "phone"].includes(field) ? T.fontMono : T.font,
                                color: bad ? "#B0093A" : field ? T.n1 : T.n3,
                                background: bad ? "#FDECEF" : warn ? "#FFFBF0" : "transparent",
                                boxShadow: bad ? "inset 0 -2px 0 #F80449" : warn ? "inset 0 -2px 0 #E4B305" : "none", padding: 0,
                              }}
                            />
                          </span>
                        );
                      })}
                    </div>
                  ))}

                  <Btn variant="link" size="sm" disabled={!canWrite} icon={<Icon.plus size={13} />} onClick={() => addBlankRows(1)}>
                    {t("locations.import.addABlankRow", "Add a blank row")}
                  </Btn>
                </div>
              </div>
            </div>

            {/* Issues panel — a right-hand column on wide viewports, a strip
                above the grid when narrow (order:-1), so the grid always owns
                the full width of the row. */}
            <div style={{ width: narrow ? "100%" : 300, flexShrink: 0, order: narrow ? -1 : 0, maxHeight: narrow ? 188 : undefined, background: T.white, borderRadius: 10, border: `1px solid ${T.border}`, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <div style={{ flexShrink: 0, padding: "12px 14px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "baseline", gap: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>{t("locations.import.issues", "Issues")}</span>
                <span style={{ fontSize: 12, fontFamily: T.fontMono, color: T.soft }}>{verdict?.issues.length ?? 0}</span>
                <span style={{ flex: 1 }} />
                {grouped.some((g) => g.fixable) ? (
                  <Btn variant="link" size="sm" disabled={checking} onClick={() => void fixAll()}>
                    {t("locations.import.fixAll", "Fix all")}
                  </Btn>
                ) : null}
              </div>

              <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                {!grouped.length ? (
                  <InlineEmptyState size="sm" icon={checking ? <Spinner size="sm" label={t("locations.import.checking", "Checking…")} /> : <Icon.check size={16} />} title={checking ? t("locations.import.checking", "Checking…") : t("locations.import.everyRowIsClean", "Every row is clean.")} />
                ) : grouped.map((g) => {
                  const tone = SEV_TONE[g.severity] ?? SEV_TONE.info;
                  return (
                    <div key={`${g.code}:${g.field}`} style={{ padding: "12px 14px", borderBottom: `1px solid ${T.n6}` }}>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                        <span style={{ fontSize: 15, fontWeight: 700, color: tone.hue, fontVariantNumeric: "tabular-nums" }}>{g.rows.length}</span>
                        <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 600, color: T.n1, lineHeight: 1.35 }}>{g.message}</span>
                      </div>
                      <div style={{ marginTop: 5, fontSize: 11.5, color: T.soft, lineHeight: 1.45 }}>
                        {t("locations.import.rowsColon", "Rows")} {g.rows.slice(0, 8).join(", ")}{g.rows.length > 8 ? t("locations.import.text2", "+{arg1}", { arg1: g.rows.length - 8 }) : ""}
                      </div>
                      {g.fixable ? (
                        <Btn type="button" size="sm" variant="secondary" onClick={() => void fixAll()} disabled={checking} style={{ marginTop: 8 }}>
                          {t("locations.import.applySuggestion", "Apply suggestion")} ({g.fixable})
                        </Btn>
                      ) : null}
                    </div>
                  );
                })}
              </div>

              <div style={{ flexShrink: 0, padding: "12px 14px", borderTop: `1px solid ${T.border}`, background: T.canvas }}>
                {([
                  { n: verdict?.createCount ?? 0, label: t("locations.import.willBeCreated", "will be created"), hue: "#0A8375" },
                  { n: verdict?.updateCount ?? 0, label: t("locations.import.willUpdateAnExistingStoreCode", "will update an existing store code"), hue: T.p1 },
                  { n: verdict?.rejectCount ?? 0, label: t("locations.import.goToTheRejectsFile", "go to the rejects file"), hue: "#B0093A" },
                ]).map((s) => (
                  <div key={s.label} style={{ display: "flex", alignItems: "baseline", gap: 7, marginBottom: 4 }}>
                    <span style={{ fontSize: 18, fontWeight: 700, color: s.hue, fontVariantNumeric: "tabular-nums" }}>{s.n.toLocaleString()}</span>
                    <span style={{ fontSize: 12, color: T.soft, lineHeight: 1.3 }}>{s.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </Screen>
  );
}
